import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import { paymentService } from '../services/paymentService';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PaymentCallbackPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const query = useQuery();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const reference = query.get('reference');
    const orderId = query.get('orderId');
    const paymentId = query.get('paymentId');

    const verify = async () => {
      if (!reference || (!orderId && !paymentId)) {
        setError('Missing payment reference or payment information.');
        setLoading(false);
        return;
      }

      try {
        if (paymentId) {
          await paymentService.verifyPaystackForPayment(reference, paymentId);
        } else {
          await paymentService.verifyPaystack(reference, orderId);
        }
        setSuccess(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Payment verification failed:', err);
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Payment verification failed. Please contact support if you were charged.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    // Only verify if user is logged in
    if (user) {
      verify();
    } else {
      setError('You must be logged in to verify your payment.');
      setLoading(false);
    }
  }, [query, user]);

  const handleGoToOrders = () => {
    navigate('/orders');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1548A6 0%, #64B5F6 100%)',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={8}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" component="h1" gutterBottom fontWeight="bold" color="primary.main">
              Payment Status
            </Typography>

            {loading && (
              <Box sx={{ mt: 3 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Verifying your payment, please wait...
                </Typography>
              </Box>
            )}

            {!loading && success && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Payment successful! Your order has been updated.
                </Alert>
                <Button variant="contained" color="primary" onClick={handleGoToOrders}>
                  View My Orders
                </Button>
              </Box>
            )}

            {!loading && error && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
                <Button variant="contained" color="primary" onClick={handleGoToOrders} sx={{ mr: 1 }}>
                  View My Orders
                </Button>
                <Button variant="text" color="primary" onClick={handleGoHome}>
                  Go to Home
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PaymentCallbackPage;

